import { useEffect, useState } from "react";

import { Alert, Heading, Modal, SearchField } from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { isError } from "data/ss-error";
import SettingsPolicy from "pages/settings/common/settings-policy";

import {
  ADMIN_KEYSTONE,
  type KeystonePolicySearchResult,
} from "./keystone-admin-client";
import styles from "./policy-search.module.scss";

export const PolicySearch = ({ onClose }) => {
  const { searchPolicies } = ADMIN_KEYSTONE;
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [resultCount, setResultCount] = useState(0);
  const [searchResults, setSearchResults] = useState<
    KeystonePolicySearchResult[]
  >([]);
  const recentImpersonations = JSON.parse(
    localStorage.getItem("policy_searches") ?? "[]"
  );

  useEffect(() => {
    setResultCount(0);
    setSearchResults([]);
    if (!searchText || searchText.length < 4) {
      return;
    }
    (async () => {
      setLoading(true);
      const res = await searchPolicies(searchText);
      if (!isError(res)) {
        setSearchResults(res.results);
        setResultCount(res.count);
      }
      setLoading(false);
    })();
  }, [searchPolicies, searchText]);

  const onPolicyClick = (policy) => {
    localStorage.setItem(
      "policy_searches",
      JSON.stringify([policy, ...recentImpersonations.slice(0, 4)])
    );
  };

  const results = searchText ? searchResults : recentImpersonations;

  return (
    <Modal heading="Policy search" onClose={onClose} open noFooter>
      <Alert
        appearance="danger"
        title="Caution!"
        description=" Any changes made while viewing a policy will be applied."
      />
      <div className={styles.searchWrapper}>
        <SearchField
          value={searchText}
          onChange={setSearchText}
          placeholder="Enter a policyID, quoteID, carrier name, carrierID, etc"
        />
      </div>
      <Stack className={styles.results}>
        {loading ? <Heading size="sm">Loading...</Heading> : null}
        {searchText && searchText.length <= 3 ? (
          <Heading size="sm">Keep typing (at least four characters)...</Heading>
        ) : null}
        {!loading && searchText && searchText.length > 3 && !resultCount ? (
          <Heading size="sm">No results for {searchText}</Heading>
        ) : null}
        {resultCount ? (
          <Heading size="sm">{`${resultCount} result${
            resultCount === 1 ? "" : "s"
          }`}</Heading>
        ) : null}
        {!searchText && results.length ? (
          <Heading size="sm">Recently viewed</Heading>
        ) : null}
        {results.map((result) => {
          const address =
            result.addresses.find((addr) => addr.addressType === "PROPERTY") ??
            result.addresses[0];
          const policyUrl = new URL(window.location.href);
          policyUrl.searchParams.set("policyId", result.keystonePolicyId);
          return (
            <div key={result.keystonePolicyId} className={styles.result}>
              <a
                href={policyUrl.toString()}
                onClick={() => onPolicyClick(result)}
              >
                <SettingsPolicy
                  isCommercial={result.isCommercial}
                  address={address}
                  detailItems={[
                    { label: "Policy number", value: result.platformPolicyId },
                    { label: "Status", value: result.policyState },
                    { label: "Carrier", value: result.carrier },
                  ]}
                ></SettingsPolicy>
              </a>
            </div>
          );
        })}
      </Stack>
    </Modal>
  );
};
